import { db } from '../lib/postgrest'
import { SPECIAL_SCHEMA, SPECIAL_TABLES } from '../lib/specialSchema'
import type { Database } from '../types/database.generated'

type UserTenantRow = Database[typeof SPECIAL_SCHEMA]['Views'][typeof SPECIAL_TABLES.userTenants]['Row']

export interface TenantEntry {
  /** Raw schema name used in PostgREST queries (e.g. "bb_software"). */
  schemaName: string
  /** Human-readable display name from the database (e.g. "BB Software"). */
  name: string
  /** Identifier for the dashboard template component to render for this schema. */
  dashboardTemplate: string | null
}

const userTenants = () => db.schema(SPECIAL_SCHEMA).from(SPECIAL_TABLES.userTenants)

/**
 * Fetch the list of schemas visible to the current user.
 *
 * Queries the `vaultbi.user_tenants` view and scopes by `user_id`
 * to avoid cross-user duplicates for privileged accounts.
 *
 * @param userId The internal user id (`vaultbi.users.id`).
 * @returns An array of TenantEntry objects (rows with a null schema_name are filtered out).
 * @throws {PostgrestError} on network or query errors.
 */
export async function getUserTenants(userId: string): Promise<TenantEntry[]> {
  const { data, error } = await userTenants()
    .select('*')
    .eq('user_id', userId)
    .order('schema_name', { ascending: true })

  if (error) throw error

  return (data as UserTenantRow[])
    .filter((row): row is UserTenantRow & { schema_name: string } => row.schema_name !== null)
    .map((row) => ({
      schemaName: row.schema_name,
      name: row.name ?? row.schema_name,
      dashboardTemplate: row.dashboard_template ?? null,
    }))
}
